import { useParams, Link } from 'react-router-dom';
import { services, articles } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const service = services.find((s) => s.slug === slug) || services[0];
  const relatedArticles = articles.filter((a) => a.category === service.slug);

  const handleCategoryChange = (newSlug: string) => {
    const el = document.getElementById('category-content');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <SEOHead
        title={`${service.name} - 91.cn 一站式互联网服务平台`}
        description={`91.cn ${service.name}服务：${service.description.substring(0, 120)}`}
        keywords={`91.cn,${service.name},91.cn${service.name},域名注册,网站建设,云主机,企业邮箱`}
        canonicalUrl={`https://www.91.cn/category/${service.slug}`}
      />
      <main id="category-content">
        <div className="relative w-full h-[360px] md:h-[440px] overflow-hidden">
          <img
            src={service.heroImage}
            alt={service.name}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/55"></div>
          <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6 text-center">
            <div className="w-16 h-16 md:w-18 md:h-18 rounded-2xl bg-background-50/20 backdrop-blur-sm flex items-center justify-center mb-4">
              <i className={`${service.icon} text-2xl md:text-3xl text-white`}></i>
            </div>
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3">
              {service.name}
            </h1>
            <p className="text-sm md:text-base text-white/80 max-w-lg">
              {service.description}
            </p>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb
              items={[
                { label: '服务产品', href: '/category/domain-registration' },
                { label: service.name },
              ]}
            />
          </div>
        </div>

        <div className="w-full px-4 md:px-6 lg:px-10 sticky top-16 z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200">
          <div className="max-w-7xl mx-auto flex items-center gap-1 py-3 overflow-x-auto">
            {services.map((s) => (
              <Link
                key={s.slug}
                to={`/category/${s.slug}`}
                onClick={() => handleCategoryChange(s.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all duration-200 ${
                  s.slug === service.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                }`}
              >
                {s.name}
              </Link>
            ))}
          </div>
        </div>

        <div className="w-full px-4 md:px-6 lg:px-10 py-12 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 lg:gap-14">
              <div className="lg:col-span-2">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-5">
                  服务详情
                </h2>
                <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-8">
                  {service.details}
                </p>

                <h3 className="text-lg font-semibold text-foreground-900 mb-4">
                  核心功能
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
                  {service.features.map((feat) => (
                    <div
                      key={feat}
                      className="flex items-center gap-3 p-3 rounded-lg bg-background-100"
                    >
                      <div className="w-7 h-7 rounded-full bg-accent-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-check-line text-accent-600 text-sm"></i>
                      </div>
                      <span className="text-sm text-foreground-700">{feat}</span>
                    </div>
                  ))}
                </div>

                {relatedArticles.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-foreground-900 mb-4">
                      相关文章
                    </h3>
                    <div className="flex flex-col gap-3">
                      {relatedArticles.map((article) => (
                        <Link
                          key={article.slug}
                          to={`/article/${article.slug}`}
                          className="group flex items-start gap-4 p-4 rounded-xl border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                        >
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors mb-1">
                              {article.title}
                            </h4>
                            <p className="text-xs text-foreground-500 line-clamp-2">
                              {article.summary}
                            </p>
                          </div>
                          <div className="text-xs text-foreground-400 whitespace-nowrap mt-1">
                            {article.date}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="lg:col-span-1">
                <div className="sticky top-36 bg-background-100 rounded-2xl p-6 border border-background-200/70">
                  <h3 className="text-lg font-semibold text-foreground-900 mb-4">
                    立即咨询
                  </h3>
                  <p className="text-sm text-foreground-600 mb-5 leading-relaxed">
                    专业的顾问团队为您提供一对一咨询服务，帮助您选择最适合的方案。
                  </p>
                  <div className="flex flex-col gap-3">
                    <a
                      href="tel:400-810-9191"
                      className="flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 py-2.5 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
                    >
                      <i className="ri-phone-line"></i> 400-810-9191
                    </a>
                    <a
                      href="mailto:service@91.cn"
                      className="flex items-center justify-center gap-2 bg-background-50 hover:bg-background-200 text-foreground-700 py-2.5 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-colors border border-background-200"
                    >
                      <i className="ri-mail-line"></i> 发送邮件
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}